import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Sparkles, ChevronDown } from "lucide-react";
import { LOGO_URL } from "@/lib/config";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/study-abroad", label: "Study Abroad" },
  { to: "/scholarships", label: "Scholarship" },
  {
    to: "/courses",
    label: "Courses",
    children: [
      { to: "/courses", label: "Korean Language Training program", desc: "Level 1, Level 2 & Combo" },
    ],
  },
  {
    to: "/services",
    label: "Services",
    children: [
      { to: "/services", label: "Main Services", desc: "SOP, Study Plan, Packages" },
      { to: "/additional-services", label: "Additional Services", desc: "MSME Udyam & Support" },
    ],
  },
  { to: "/apostille", label: "Apostille" },
  { to: "/alumni", label: "Alumni" },
  { to: "/about", label: "About" },
  { to: "/notice", label: "Notice" },
  { to: "/terms", label: "Terms & Conditions" },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "bg-white/95 backdrop-blur-xl shadow-[0_2px_20px_-8px_rgba(42,22,20,0.15)]" : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Veritas Sphere" className="h-11 w-auto rounded-lg object-contain bg-white p-1 ring-1 ring-brand-line" />
          <div className="leading-tight hidden xl:block">
            <div className="font-display font-extrabold text-[16px] text-brand-ink tracking-tight">Veritas Sphere</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-brand-muted mt-0.5">The trusted hub for scholarship clarity</div>
          </div>
        </Link>

        <nav className="hidden lg:flex flex-1 items-center justify-evenly gap-2 mx-3 xl:mx-5">
          {NAV.map((n) => {
            if (n.children) {
              const isChildActive = n.children.some((c) => location.pathname === c.to);
              return (
                <div key={n.label} className="relative group py-2">
                  <NavLink
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      `link-underline text-[13px] font-medium transition-colors whitespace-nowrap inline-flex items-center gap-1 ${
                        isActive || isChildActive ? "text-brand font-semibold" : "text-brand-ink/80 hover:text-brand"
                      }`
                    }
                    end={false}
                  >
                    {n.label}
                    <ChevronDown size={12} className="transition-transform duration-200 group-hover:rotate-180 text-brand-muted group-hover:text-brand" />
                  </NavLink>

                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-1 w-52 hidden group-hover:block transition-all z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-brand-line p-1.5 flex flex-col gap-1 backdrop-blur-xl">
                      {n.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          data-testid={`nav-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={({ isActive }) =>
                            `px-3 py-2 rounded-lg text-xs font-medium transition-colors flex flex-col ${
                              isActive ? "bg-brand/10 text-brand font-semibold" : "text-brand-ink/80 hover:bg-brand-cream hover:text-brand"
                            }`
                          }
                          end={child.to === "/services"}
                        >
                          <span>{child.label}</span>
                          {child.desc && <span className="text-[10px] text-brand-muted font-normal mt-0.5">{child.desc}</span>}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline text-[13px] font-medium transition-colors whitespace-nowrap ${
                    isActive ? "text-brand font-semibold" : "text-brand-ink/80 hover:text-brand"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2.5 shrink-0">
          <Link to="/claritas" className="px-3.5 py-1.5 rounded-full bg-brand-ink text-white text-[11px] font-semibold hover:bg-brand transition-colors inline-flex items-center gap-1.5 shadow-sm" data-testid="nav-cta-claritas">
            <Sparkles size={12} className="text-brand" /> Claritas MUN
          </Link>
        </div>

        <button
          className="lg:hidden p-2 rounded-md text-brand-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-white">
          <div className="px-5 py-5 flex flex-col gap-4">
            <Link to="/claritas" className="px-4 py-2.5 rounded-xl bg-brand-ink text-white text-sm font-semibold flex items-center justify-between" data-testid="nav-mobile-claritas">
              <span className="flex items-center gap-2"><Sparkles size={14} className="text-brand" /> Claritas MUN Hub</span>
              <span>→</span>
            </Link>
            {NAV.map((n) => {
              if (n.children) {
                const isChildActive = n.children.some((c) => location.pathname === c.to);
                return (
                  <div key={n.label} className="flex flex-col gap-2">
                    <NavLink
                      to={n.to}
                      data-testid={`nav-mobile-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`text-[15px] font-semibold flex items-center justify-between ${
                        isChildActive ? "text-brand" : "text-brand-ink/85"
                      }`}
                      end={false}
                    >
                      <span>{n.label}</span>
                    </NavLink>
                    <div className="pl-3 flex flex-col gap-2 border-l-2 border-brand-line ml-1">
                      {n.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          data-testid={`nav-mobile-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={({ isActive }) =>
                            `text-[14px] font-medium ${isActive ? "text-brand font-semibold" : "text-brand-ink/75 hover:text-brand"}`
                          }
                          end={child.to === "/services"}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-mobile-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-[15px] font-medium ${isActive ? "text-brand" : "text-brand-ink/85"}`
                  }
                  end={n.to === "/"}
                >
                  {n.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="relative overflow-hidden bg-brand-ink text-white" data-testid="site-footer">
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10 relative">
        <div className="md:col-span-1">
          <div className="flex items-center">
            <img src={LOGO_URL} alt="Veritas Sphere" className="h-16 w-auto rounded-xl object-contain bg-white p-1.5 ring-1 ring-white/20" />
          </div>
          <p className="mt-5 text-white/70 text-sm leading-relaxed">
            Personalised end-to-end guidance for international scholarships, study-abroad admissions and apostille services.
          </p>
          <div className="mt-5 flex gap-3">
            {[Facebook, Instagram, Linkedin].map((Ic, i) => (
              <a
                key={i}
                href="#"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand grid place-items-center transition-colors"
                aria-label="social"
              >
                <Ic size={16} strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>
        <div>
          <div className="text-white/50 text-[11px] uppercase tracking-[0.18em] mb-4">Quick Links</div>
          <ul className="space-y-3 text-sm">
            {NAV.map((n) => {
              if (n.children) {
                return (
                  <React.Fragment key={n.label}>
                    <li>
                      <Link to={n.to} className="text-white/85 hover:text-brand transition-colors">{n.label}</Link>
                    </li>
                    {n.children.filter((c) => c.to !== n.to).map((child) => (
                      <li key={child.to} className="pl-3.5">
                        <Link to={child.to} className="text-white/70 hover:text-brand transition-colors text-xs flex items-center gap-1">
                          <span className="text-brand/60">└</span> {child.label}
                        </Link>
                      </li>
                    ))}
                  </React.Fragment>
                );
              }
              return (
                <li key={n.to}>
                  <Link to={n.to} className="text-white/85 hover:text-brand transition-colors">{n.label}</Link>
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <div className="text-white/50 text-[11px] uppercase tracking-[0.18em] mb-4">Services</div>
          <ul className="space-y-3 text-sm text-white/85">
            <li>Personal Statement / SOP</li>
            <li>Study Plan Writing</li>
            <li>Scholarship Guidance</li>
            <li>Apostille & Attestation</li>
            <li>Admission & Application</li>
          </ul>
        </div>
        <div>
          <div className="text-white/50 text-[11px] uppercase tracking-[0.18em] mb-4">Contact</div>
          <ul className="space-y-3 text-sm text-white/85">
            <li className="flex items-start gap-2"><Phone size={15} className="mt-0.5" strokeWidth={1.5} /> +91 94661 45196</li>
            <li className="flex items-start gap-2"><Mail size={15} className="mt-0.5" strokeWidth={1.5} /> veritassphere26@gmail.com</li>
            <li className="flex items-start gap-2"><MapPin size={15} className="mt-0.5" strokeWidth={1.5} /> Mon–Sat · 10am – 6pm</li>
            <li className="text-white/60 text-xs mt-3">Udyam Registration No.:<br/>UDYAM-DL-05-0089519</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-white/50 text-xs">
        © {new Date().getFullYear()} Veritas Sphere — All Rights Reserved
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-[72px]">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
